import { api } from '../api';
import { Link } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import { Empty, hoursOf, Loading, when } from '../components/bits';
import { money } from './Billing';

/** One document, laid out to be read by somebody who is not in the app.
 *
 *  This is how a draft or a statement reaches a client: it prints, and a
 *  printed page becomes a PDF and then an attachment. There is no sending
 *  here, and nothing on this page is addressed to the reader — it is the
 *  studio's own document until somebody decides to send it. */
export function Document({ id }: { id: number }) {
  const { t, locale } = useSession();
  const doc = useData(() => api.document(id), [id]);

  if (doc.error) return <Shell><Empty>{t.notFound}</Empty></Shell>;
  if (!doc.data) return <Shell><Loading /></Shell>;

  const d = doc.data;
  const statement = d.kind === 'statement';
  const minutes = (d.lines ?? []).reduce((sum, l) => sum + l.minutes, 0);

  return (
    <Shell width="narrow">
      <div className="page-head no-print">
        <div className="grow">
          <Link to="/billing" className="link">&lt; {t.billing}</Link>
        </div>
        <button onClick={() => window.print()}>{t.print}</button>
      </div>

      <article className="document">
        <header>
          <p className="kicker">{statement ? t.statement : t.invoiceDraft}</p>
          <h1>{d.workspaceName}</h1>
          <p className="muted">
            {d.periodStart && when(d.periodStart, locale)}
            {d.periodEnd && d.periodEnd !== d.periodStart && ` – ${when(d.periodEnd, locale)}`}
          </p>
        </header>

        {statement && <p className="dim">{t.paidUpFront}</p>}
        {d.note && <p className="dim">{d.note}</p>}

        <table>
          <thead>
            <tr>
              <th>{t.project}</th>
              <th className="num">{t.hours}</th>
              {!statement && <th className="num">{t.rate}</th>}
              {!statement && <th className="num">{t.amount}</th>}
            </tr>
          </thead>
          <tbody>
            {(d.lines ?? []).map((l) => (
              <tr key={l.id}>
                <td>{l.description}</td>
                <td className="num">{hoursOf(l.minutes)}</td>
                {!statement && (
                  <td className="num muted">{money(l.rateCents, locale)}{t.perHour}</td>
                )}
                {!statement && <td className="num">{money(l.amountCents, locale)}</td>}
              </tr>
            ))}
            <tr className="total">
              <td>{t.total}</td>
              <td className="num">{hoursOf(minutes)}</td>
              {!statement && <td />}
              {!statement && <td className="num">{money(d.totalCents, locale)}</td>}
            </tr>
          </tbody>
        </table>

        <footer className="muted">
          {d.externalRef && <p>{d.externalRef}</p>}
          <p>Omibee · {when(d.createdAt, locale)}</p>
        </footer>
      </article>
    </Shell>
  );
}
